'use client'

import { useState } from 'react'
import { useLocale } from '@/lib/i18n/use-locale'
import { getCsrfHeaders } from '@/lib/utils/csrf'

interface RotatorFormData {
  customCode: string
  rotationType: 'sequential' | 'round-robin' | 'random' | 'weighted'
  destinationText: string
  expiresIn?: number
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  utmTerm?: string
  utmContent?: string
}

interface URLRotatorFormProps {
  onSuccess?: (rotation: any) => void
}

export function URLRotatorForm({ onSuccess }: URLRotatorFormProps) {
  const { t } = useLocale()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)
  const [utmPreset, setUtmPreset] = useState('manual')

  const [formData, setFormData] = useState<RotatorFormData>({
    customCode: '',
    rotationType: 'sequential',
    destinationText: '',
  })

  const normalizeUtmValue = (value: string) => value.replace(/\s+/g, '+')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setSuccess(false)

    // Validation
    const destinations = formData.destinationText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean)

    if (destinations.length < 2) {
      setError(t('rotator.minUrlsError'))
      return
    }

    if (destinations.length > 5) {
      setError(t('rotator.maxUrlsError'))
      return
    }

    setLoading(true)

    try {
      const urls = destinations.map((url, index) => ({
        url,
        weight: formData.rotationType === 'weighted' ? 1 : 1,
        order: formData.rotationType === 'sequential' ? index + 1 : 0,
      }))

      const payload = {
        customCode: formData.customCode || undefined,
        rotationType: formData.rotationType,
        urls,
        expiresIn: formData.expiresIn || undefined,
        utmSource: formData.utmSource || undefined,
        utmMedium: formData.utmMedium || undefined,
        utmCampaign: formData.utmCampaign || undefined,
        utmTerm: formData.utmTerm || undefined,
        utmContent: formData.utmContent || undefined,
      }

      const response = await fetch('/api/rotations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(await getCsrfHeaders()),
        },
        body: JSON.stringify(payload),
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || t('rotator.errorCreating'))
      }

      const rotation = await response.json()
      setSuccess(true)
      setError(null)

      // Reset form
      setFormData({
        customCode: '',
        rotationType: 'sequential',
        destinationText: '',
      })

      if (onSuccess) {
        onSuccess(rotation)
      }
    } catch (err: any) {
      setError(err.message || t('rotator.errorCreating'))
      setSuccess(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Rotator Slug */}
      <div>
        <label htmlFor="customCode" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('rotator.slugLabel')}
        </label>
        <div className="flex items-center gap-2 rounded-lg border px-4 py-2 dark:border-gray-700 dark:bg-gray-800">
          <span className="text-xs text-gray-500 dark:text-gray-400">/r/</span>
          <input
            type="text"
            id="customCode"
            value={formData.customCode}
            onChange={(e) => setFormData({ ...formData, customCode: e.target.value })}
            placeholder={t('rotator.slugPlaceholder')}
            className="w-full bg-transparent text-sm text-gray-900 outline-none dark:text-white"
          />
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('rotator.slugHelp')}</p>
      </div>

      {/* Rotation Type */}
      <div>
        <label htmlFor="rotationType" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('rotator.typeLabel')}
        </label>
        <select
          id="rotationType"
          value={formData.rotationType}
          onChange={(e) =>
            setFormData({ ...formData, rotationType: e.target.value as RotatorFormData['rotationType'] })
          }
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          <option value="sequential">{t('rotator.typeSequential')}</option>
          <option value="round-robin">{t('rotator.typeRoundRobin')}</option>
          <option value="random">{t('rotator.typeRandom')}</option>
          <option value="weighted">{t('rotator.typeWeighted')}</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {formData.rotationType === 'sequential' && t('rotator.typeSequentialHelp')}
          {formData.rotationType === 'round-robin' && t('rotator.typeRoundRobinHelp')}
          {formData.rotationType === 'random' && t('rotator.typeRandomHelp')}
          {formData.rotationType === 'weighted' && t('rotator.typeWeightedHelp')}
        </p>
      </div>

      {/* Destination URLs */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('rotator.destinationsTitle')}
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">{t('rotator.destinationsHelp')}</p>
        <textarea
          value={formData.destinationText}
          onChange={(e) => setFormData({ ...formData, destinationText: e.target.value })}
          placeholder={t('rotator.destinationsPlaceholder')}
          className="min-h-[140px] w-full rounded-lg border px-4 py-3 text-sm text-gray-900 shadow-sm outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white"
          required
        />
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
          {t('rotator.destinationsHint')}
        </p>
      </div>

      {/* Expiration (Optional) */}
      <div>
        <label htmlFor="expiresIn" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {t('rotator.expirationLabel')}
        </label>
        <select
          id="expiresIn"
          value={formData.expiresIn ? String(formData.expiresIn) : ''}
          onChange={(e) =>
            setFormData({
              ...formData,
              expiresIn: e.target.value ? parseInt(e.target.value, 10) : undefined,
            })
          }
          className="w-full px-4 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
        >
          <option value="">Never expire</option>
          <option value="1">1 hour</option>
          <option value="24">24 hours</option>
          <option value="168">7 days</option>
          <option value="720">30 days</option>
        </select>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{t('rotator.expirationHelp')}</p>
      </div>

      {/* UTM Parameters (Optional) */}
      <details className="border rounded-lg p-4 dark:border-gray-700">
        <summary className="cursor-pointer font-medium text-gray-700 dark:text-gray-300">
          {t('rotator.utmTitle')}
        </summary>
        <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 mb-3">{t('rotator.utmHelp')}</p>
        <div className="space-y-3 mt-3">
          <div>
            <label htmlFor="rotator-utm-preset" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              Popular presets
            </label>
            <select
              id="rotator-utm-preset"
              value={utmPreset}
              onChange={(e) => {
                const value = e.target.value
                setUtmPreset(value)
                if (value === 'manual') {
                  setFormData({
                    ...formData,
                    utmSource: undefined,
                    utmMedium: undefined,
                    utmCampaign: undefined,
                    utmTerm: undefined,
                    utmContent: undefined,
                  })
                  return
                }
                const presets: Record<string, { source: string; medium: string; campaign: string }> = {
                  newsletter: { source: 'newsletter', medium: 'email', campaign: 'monthly-newsletter' },
                  facebook: { source: 'facebook', medium: 'social', campaign: 'social-post' },
                  facebookads: { source: 'facebook', medium: 'paid-social', campaign: 'facebook-ads' },
                  instagram: { source: 'instagram', medium: 'social', campaign: 'social-post' },
                  x: { source: 'x', medium: 'social', campaign: 'social-post' },
                  linkedin: { source: 'linkedin', medium: 'social', campaign: 'social-post' },
                  googleads: { source: 'google', medium: 'cpc', campaign: 'search-ads' },
                  whatsapp: { source: 'whatsapp', medium: 'messaging', campaign: 'whatsapp-share' },
                  telegram: { source: 'telegram', medium: 'messaging', campaign: 'telegram-share' },
                  line: { source: 'line', medium: 'messaging', campaign: 'line-share' },
                  tiktokads: { source: 'tiktok', medium: 'paid-social', campaign: 'tiktok-ads' },
                  tiktoksocial: { source: 'tiktok', medium: 'social', campaign: 'tiktok-social' },
                }
                const preset = presets[value]
                if (preset) {
                  setFormData({
                    ...formData,
                    utmSource: preset.source,
                    utmMedium: preset.medium,
                    utmCampaign: preset.campaign,
                    utmTerm: undefined,
                    utmContent: undefined,
                  })
                }
              }}
              className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
            >
              <option value="manual">Manual / None</option>
              <option value="newsletter">Newsletter (email)</option>
              <option value="facebook">Facebook (social)</option>
              <option value="facebookads">Facebook Ads (paid social)</option>
              <option value="instagram">Instagram (social)</option>
              <option value="x">X / Twitter (social)</option>
              <option value="linkedin">LinkedIn (social)</option>
              <option value="googleads">Google Ads (cpc)</option>
              <option value="whatsapp">WhatsApp (messaging)</option>
              <option value="telegram">Telegram (messaging)</option>
              <option value="line">Line Messenger (messaging)</option>
              <option value="tiktokads">TikTok Ads (paid social)</option>
              <option value="tiktoksocial">TikTok Social (social)</option>
            </select>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <label htmlFor="rotator-utm-source" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                utm_source
              </label>
              <input
                type="text"
                id="rotator-utm-source"
                value={formData.utmSource || ''}
                onChange={(e) => {
                  setUtmPreset('manual')
                  setFormData({ ...formData, utmSource: normalizeUtmValue(e.target.value) })
                }}
                placeholder="newsletter"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="rotator-utm-medium" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                utm_medium
              </label>
              <input
                type="text"
                id="rotator-utm-medium"
                value={formData.utmMedium || ''}
                onChange={(e) => {
                  setUtmPreset('manual')
                  setFormData({ ...formData, utmMedium: normalizeUtmValue(e.target.value) })
                }}
                placeholder="email"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Channels: cpc (paid ads), email, social, organic, affiliate, referral
              </p>
            </div>
            <div>
              <label htmlFor="rotator-utm-campaign" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                utm_campaign
              </label>
              <input
                type="text"
                id="rotator-utm-campaign"
                value={formData.utmCampaign || ''}
                onChange={(e) => {
                  setUtmPreset('manual')
                  setFormData({ ...formData, utmCampaign: normalizeUtmValue(e.target.value) })
                }}
                placeholder="spring-launch"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Descriptive names: spring_sale_2026, product_launch_x, newsletter_q1, black_friday
              </p>
            </div>
            <div>
              <label htmlFor="rotator-utm-term" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                utm_term
              </label>
              <input
                type="text"
                id="rotator-utm-term"
                value={formData.utmTerm || ''}
                onChange={(e) => {
                  setUtmPreset('manual')
                  setFormData({ ...formData, utmTerm: normalizeUtmValue(e.target.value) })
                }}
                placeholder="running-shoes"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Target Keywords: running+shoes, marketing+software
              </p>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="rotator-utm-content" className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                utm_content
              </label>
              <input
                type="text"
                id="rotator-utm-content"
                value={formData.utmContent || ''}
                onChange={(e) => {
                  setUtmPreset('manual')
                  setFormData({ ...formData, utmContent: normalizeUtmValue(e.target.value) })
                }}
                placeholder="cta-button"
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              />
              <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
                Element descriptors: header_button, text_link, banner_ad_blue, video_ad
              </p>
            </div>
          </div>
        </div>
      </details>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-600 dark:text-green-400 text-sm">
          {t('rotator.successCreated')}
        </div>
      )}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={loading}
        className="w-full px-6 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-medium rounded-lg transition-colors"
      >
        {loading ? t('rotator.creating') : t('rotator.createButton')}
      </button>
    </form>
  )
}
